import * as React from "react";
import { Link } from "react-router-dom";
import { useCurrentStudyConfigurationContext } from "../../Core/CurrentStudyConfigurationContext";
import { useSecurityContext } from "../../Core/SecurityContext";
import { PermissionEnum } from "../../misc/api/ApiPermissionEnum";
import { EDiaryConfiguration, isModelWithError, Query } from "../../misc/api/apiTypes";
import { useWebPortalLegacyClient } from "../../misc/api/WebPortalTokenClient";
import Alert, { AlertCategoryEnum } from "../../misc/components/Alert";
import { Authorization } from "../../misc/components/Authorization";
import { DateTimeAccuracy, LocalizedDateTime } from "../../misc/components/LocalizedDateTime";
import Modal from "../../misc/components/modal";
import { PageAction, QueryTableActions, QueryTableState, ReceiveQueryAction, SortAction } from "../../misc/components/queryTable/Hooks/UseQueryTable";
import { QueryTable, QueryTableColumn } from "../../misc/components/queryTable/QueryTable";

interface EDiaryConfigurationsTableProps {
    queryTableReducer: [QueryTableState, React.Dispatch<QueryTableActions>];
}

export const DiaryConfigurationsTable = ({ queryTableReducer }: EDiaryConfigurationsTableProps) => {
    const [isLoading, setIsLoading] = React.useState(false);
    const [selectedFilterModal, setSelectedFilterModal] = React.useState<string>(null);
    const [deleteModalVisible, setDeleteModalVisible] = React.useState<EDiaryConfiguration>();
    const [errorAlert, setErrorAlert] = React.useState<string>();

    const { study } = useCurrentStudyConfigurationContext();
    const { currentUser, currentUserPermissions } = useSecurityContext();
    const legacyClient = useWebPortalLegacyClient();

    const [queryState, tableDispatch] = queryTableReducer;
    const updateQueryData = (query: Query<EDiaryConfiguration>) => tableDispatch(ReceiveQueryAction.create(query));

    React.useEffect(() => {
        setIsLoading(true);

        legacyClient.getEDiaryConfigurations(queryState.query).then((_populatedQuery) => {
            updateQueryData(_populatedQuery);
            setIsLoading(false);
        });

    }, [queryState.query.Page.Current, queryState.query.Order.Member, queryState.query.Order.Direction, queryState.query.Filters]);

    const handleFilter = (member: string) => setSelectedFilterModal(member);
    const handlePage = (page: number) => tableDispatch(PageAction.create(page));
    const handleSort = (member: string) => tableDispatch(SortAction.create(member));

    const handleDelete = async () => {
        try {
            const result = await legacyClient.deleteEDiaryConfiguration(deleteModalVisible.Id);
        } catch (ex) {
            setErrorAlert(ex.Message)
        }


        setDeleteModalVisible(undefined);

        legacyClient.getEDiaryConfigurations(queryState.query).then((_populatedQuery) => {
            updateQueryData(_populatedQuery);
            setIsLoading(false);
        });
    }

    const RenderMenu = (
        row: EDiaryConfiguration,
        currentPermissions
    ) => {
        return (
            <Authorization currentPermissions={currentUserPermissions} studyId={study.id} permissionIdsAny={[PermissionEnum.EditEDiaryConfiguration, PermissionEnum.DeleteEDiaryConfiguration]}>
                <div className="text-center">
                    <div className="btn-group" role="group">
                        <button
                            type="button"
                            className="btn btn-primary btn-xs dropdown-toggle"
                            data-toggle="dropdown"
                            aria-haspopup="true"
                            aria-expanded="false"
                        >
                            <i className="fa fa-bars" aria-hidden="true" />
                        </button>
                        <ul className="dropdown-menu pull-right scrollable-menu" role="menu">
                            <Authorization
                                permissionId={PermissionEnum.EditEDiaryConfiguration}
                                studyId={study.id}
                                currentPermissions={currentPermissions}
                            >
                                <li>
                                    <Link to={`/DiaryConfigurations/Edit/${row.Id}`}>Edit</Link>
                                </li>
                            </Authorization>
                            <Authorization
                                permissionId={PermissionEnum.EditEDiaryConfiguration}
                                studyId={study.id}
                                currentPermissions={currentPermissions}
                            >
                                <li>
                                    <Link to={`/DiaryConfigurations/Files/${row.Id}`}>Files</Link>
                                </li>
                            </Authorization>
                            <Authorization
                                permissionId={PermissionEnum.DeleteEDiaryConfiguration}
                                studyId={study.id}
                                currentPermissions={currentPermissions}
                            >
                                <li>
                                    <a onClick={() => setDeleteModalVisible(row)}>Delete</a>
                                </li>
                            </Authorization>
                        </ul>
                    </div>
                </div>
            </Authorization>
        );
    };

    return (
        <>
            <Alert show={errorAlert !== undefined} category={AlertCategoryEnum.Danger}>
                {errorAlert}
            </Alert>
            <QueryTable id="DiaryConfigurationsTable" query={queryState.query} rowUniqueIdentifier={(row) => row.Id} onPage={handlePage} onSort={handleSort} onFilter={handleFilter} isLoading={isLoading}>
                <QueryTableColumn
                    member="Name"
                    header="Name"
                    map={(row) => row.Name}
                    sortable={true}
                    filterable={false}
                />
                <QueryTableColumn
                    member="CreatedDateTime"
                    header="Created"
                    map={(row) => <LocalizedDateTime time={row.LastModifiedDateTime} accuracy={DateTimeAccuracy.Minute} isTooltip={true} />}
                    sortable={true}
                    filterable={false}
                />
                <QueryTableColumn
                    member="LastModifiedDateTime"
                    header="Last Modified"
                    map={(row) => <LocalizedDateTime time={row.LastModifiedDateTime} accuracy={DateTimeAccuracy.Minute} isTooltip={true} />}
                    sortable={true}
                    filterable={false}
                />
                <QueryTableColumn
                    member=""
                    header=""
                    map={row => RenderMenu(
                        row,
                        currentUserPermissions
                    )
                    }
                />
            </QueryTable>
            {deleteModalVisible ? <Modal
                header="Delete Configuration"
                show={true}
            >
                <div className="modal-body">
                    Are you sure you wish to delete the configuation '{deleteModalVisible.Name}'?
                </div>
                <div className="modal-footer">
                    <button className="btn btn-danger" onClick={handleDelete}>Delete</button>
                    <button className="btn btn-primary btn-outline" onClick={() => setDeleteModalVisible(undefined)}>Cancel</button>
                </div>
            </Modal> : null}

        </>
    );
}
